import { useMemo, useState } from "react"
import { Check, Search, X } from "lucide-react"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { CategoryOption } from "@/lib/queries/categories"

/**
 * CategoryMultiSelect — inline filterable chips for picking multiple
 * categories. Replaces the legacy admin's antd Select mode="multiple"
 * pattern without pulling in a Popover/Combobox dependency.
 *
 * UX:
 *   - Selected categories shown as filled chips at the top with an X
 *   - Search field filters the list of options below
 *   - Clicking an option toggles its selection; selected options have a
 *     check mark and a different background
 *
 * Cap: with thousands of categories the inline list would get unwieldy;
 * if usage data shows that, swap this for a Popover-based combobox later.
 */
export function CategoryMultiSelect({
  options,
  value,
  onChange,
  loading,
  disabled,
}: {
  options: CategoryOption[]
  value: string[]
  onChange: (ids: string[]) => void
  loading?: boolean
  disabled?: boolean
}) {
  const [filter, setFilter] = useState("")

  const valueSet = useMemo(() => new Set(value), [value])

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.name.toLowerCase().includes(q))
  }, [options, filter])

  const selected = useMemo(
    () => options.filter((o) => valueSet.has(o._id)),
    [options, valueSet]
  )

  const toggle = (id: string) => {
    if (disabled) return
    if (valueSet.has(id)) {
      onChange(value.filter((v) => v !== id))
    } else {
      onChange([...value, id])
    }
  }

  return (
    <div className="space-y-2 rounded-md border bg-background p-3">
      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((c) => (
            <button
              key={c._id}
              type="button"
              onClick={() => toggle(c._id)}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-0.5 text-xs text-primary-foreground hover:bg-primary/90"
            >
              {c.name}
              <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Filter categories…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-8 pl-7 text-sm"
          disabled={disabled}
        />
      </div>

      {/* Options list */}
      <div className="max-h-48 overflow-auto rounded border bg-muted/20 p-1 text-sm">
        {loading && (
          <div className="px-2 py-1 text-muted-foreground">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-2 py-1 text-muted-foreground">
            {options.length === 0 ? "No categories" : "No match"}
          </div>
        )}
        {filtered.map((o) => {
          const checked = valueSet.has(o._id)
          return (
            <button
              key={o._id}
              type="button"
              onClick={() => toggle(o._id)}
              disabled={disabled}
              className={cn(
                "flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left transition-colors",
                checked
                  ? "bg-primary/10 text-foreground"
                  : "hover:bg-secondary/60"
              )}
            >
              <span>{o.name}</span>
              {checked && <Check className="h-4 w-4 text-primary" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}
